import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { IdNameList } from "../../../../../../../../api/services/common/models";
import {
  useCreateProjectExpenseMutation,
  useEditProjectExpenseDetails,
} from "../../../../../../../../api/services/project/projectApis";
import {
  ITabs,
  MuiBox,
  MuiModal,
  MuiRadioGroup,
  MuiTabPanel,
  MuiTabs,
  MuiTypography,
} from "../../../../../../../../components";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import { KeyValue } from "../../../../../../../../helpers/types/common";
import { replaceHashWithValue } from "../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../redux/slices/appState/appStateSlice";
import { CancelFieldModal } from "../../../../../../../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";

import { ViewProjectFeeOrExpenseDetails } from "../../../../../../../../api/services/project/model";
import {
  getProjectAddFeesOrExpensePayload,
  setFormProjectExpenseData,
} from "../../../../../../helper";
import {
  ADD_PROJECT_FEE_AND_EXPENSE_TYPES,
  ProjectAddOrEditExpenseOrFeeFormKey,
  projectAddExpenseFormInitialValue,
} from "../../../../../../modelsAndSchemas/Models";
import { ProjectAddOrEditExpenseOrFeeSchema } from "../../../../../../modelsAndSchemas/ValidationSchema";
import { projectAddFeesAndExpensesSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { ProjectAddFeesAndExpenseTabWrapper } from "../../ProjectFeesAndExpenseMain.style";
import { ProjectAddFeesAndExpenseGeneralForm } from "./ProjectAddFeesAndExpenseGeneralForm";
import { ProjectAddFeesAndExpenseSummary } from "./ProjectAddFeesAndExpenseSummary";
import { ProjectAddFeesAndExpenseTaxForm } from "./ProjectAddFeesAndExpenseTaxForm";

export interface ProjectAddFeesAndExpenseProps {
  showProjectAddFeesAndExpenseModal: boolean;
  onHideProjectAddFeesAndExpenseModal: (isReloadList?: boolean) => void;
  billingCurrencyId: string;
  billingCurrencyName: string;
  currencyList: IdNameList[];
  projectId: string;
  selectedExpenseDetails?: ViewProjectFeeOrExpenseDetails | null;
}

const TAB_INDEX = {
  GENERAL: 0,
  TAX: 1,
  SUMMARY: 2,
};

export const ProjectAddFeesAndExpense = ({
  onHideProjectAddFeesAndExpenseModal,
  showProjectAddFeesAndExpenseModal,
  projectId,
  billingCurrencyId,
  billingCurrencyName,
  selectedExpenseDetails,
  currencyList,
}: ProjectAddFeesAndExpenseProps) => {
  const {
    useFetchLabel,
    commonLabels,
    commonValidationMessages,
    fetchComponentTitle,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const [selectedTab, setSelectedTab] = useState(TAB_INDEX.GENERAL);
  const [state, setState] = useState<{
    showCancelModal: boolean;
    showTypeChangeModal: boolean;
    type: string;
  }>({
    showCancelModal: false,
    showTypeChangeModal: false,
    type:
      selectedExpenseDetails?.type ??
      ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_OR_PROJECT_FEE,
  });
  const dispatch = useDispatch();
  const {
    mutateAsync: createProjectExpenseMutation,
    isPending: isCreatingProjectExpense,
    isSuccess: isCreateExpenseSuccess,
    isError: isCreateExpenseError,
  } = useCreateProjectExpenseMutation();

  const {
    mutateAsync: editProjectExpenseMutation,
    isPending: isEditingProjectExpense,
    isSuccess: isEditExpenseSuccess,
    isError: isEditExpenseError,
  } = useEditProjectExpenseDetails();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  useEffect(() => {
    const isApiLoading = isCreatingProjectExpense || isEditingProjectExpense;
    if (isApiLoading) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    } else {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isCreatingProjectExpense, isEditingProjectExpense]);

  useEffect(() => {
    if (selectedExpenseDetails) {
      setFormProjectExpenseData(
        selectedExpenseDetails,
        setValue,
        billingCurrencyName,
        userCountryDateFormat,
      );
    }
  }, [selectedExpenseDetails]);

  useEffect(() => {
    const isCreateOrEditApiSuccess =
      isCreateExpenseSuccess || isEditExpenseSuccess;
    if (isCreateOrEditApiSuccess) {
      onHideProjectAddFeesAndExpenseModal(true);
    }
  }, [isCreateExpenseSuccess, isEditExpenseSuccess]);

  useEffect(() => {
    const isApiError = isCreateExpenseError || isEditExpenseError;
    if (isApiError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isCreateExpenseError, isEditExpenseError]);

  const getLabel = useFetchLabel(
    projectAddFeesAndExpensesSiteCoreKeys.addFeesAndExpenses,
  );

  const tabIdGeneral = `general-tab`;
  const tabIdTax = `tax-tab`;
  const tabIdSummary = `summary-tab`;
  const tabs: ITabs[] = [
    {
      id: tabIdGeneral,
      label: getLabel(projectAddFeesAndExpensesSiteCoreKeys.generalTab),
    },
    {
      id: tabIdTax,
      label: getLabel(projectAddFeesAndExpensesSiteCoreKeys.taxTab),
    },
    {
      id: tabIdSummary,
      label: getLabel(projectAddFeesAndExpensesSiteCoreKeys.summaryTab),
    },
  ];

  const formMethod = useForm<KeyValue>({
    mode: "all",
    defaultValues: projectAddExpenseFormInitialValue,
    resolver: yupResolver(
      ProjectAddOrEditExpenseOrFeeSchema(commonValidationMessages),
    ),
  });

  const {
    getValues,
    trigger,
    setValue,
    reset,
    formState: { dirtyFields },
    setError,
    clearErrors,
  } = formMethod;

  const isFormDirty = Object.keys(dirtyFields).length > 0;

  const prepopulatedDefaultField = () => {
    setValue(
      ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrency,
      billingCurrencyId,
    );
    setValue(
      ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyName,
      billingCurrencyName,
    );
    if (!selectedExpenseDetails) {
      setValue(
        ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseCurrencyId,
        billingCurrencyId,
      );
      setValue(
        ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseCurrencyName,
        billingCurrencyName,
      );
    }
  };

  useEffect(() => {
    prepopulatedDefaultField();
  }, [billingCurrencyName]);

  const radioOptions = [
    {
      value: ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_OR_PROJECT_FEE,
      label: getLabel(
        projectAddFeesAndExpensesSiteCoreKeys.programProjectFeeRadioButton,
      ),
    },
    {
      value: ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_EXPENSE,
      label: getLabel(projectAddFeesAndExpensesSiteCoreKeys.expenseRadioButton),
    },
  ];

  const resetFields = () => {
    reset();
    prepopulatedDefaultField();
  };

  const programProjectFeeRadioButtonLabel = getLabel(
    projectAddFeesAndExpensesSiteCoreKeys.programProjectFeeRadioButton,
  );

  const expenseRadioButtonLabel = getLabel(
    projectAddFeesAndExpensesSiteCoreKeys.expenseRadioButton,
  );

  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isFormDirty) {
      setState((prev) => ({
        ...prev,
        showTypeChangeModal: true,
      }));
      return;
    }
    const type = e.target.value;
    resetFields();
    setState((prev) => ({
      ...prev,
      type,
    }));
    setValue(ProjectAddOrEditExpenseOrFeeFormKey.projectType, type);
  };

  const validateGeneralForm = async () => {
    const isValid = (await trigger()) && checkIsConvertButtonClicked();
    if (isValid) {
      setSelectedTab(TAB_INDEX.TAX);
    }
  };
  const onAddFeesOrExpense = () => {
    const formData = getValues();
    const payload = getProjectAddFeesOrExpensePayload(
      formData,
      projectId,
      userCountryDateFormat,
    );
    if (selectedExpenseDetails)
      editProjectExpenseMutation({
        params: payload,
        expenseId: selectedExpenseDetails.expenseDetails.id,
      });
    else
      createProjectExpenseMutation({
        params: payload,
        projectId,
      });
  };

  const checkIsConvertButtonClicked = () => {
    const isRateConverted = getValues(
      ProjectAddOrEditExpenseOrFeeFormKey.projectIsRateConverted,
    );

    const billingCurrencyId = getValues(
      ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrency,
    );

    const currencyId = getValues(
      ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseCurrencyId,
    );

    const checkIsExpenseOrFeeCurrencyMismatch =
      currencyId && currencyId !== billingCurrencyId;

    if (!isRateConverted && checkIsExpenseOrFeeCurrencyMismatch) {
      setError(
        ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate,
        {
          type: "custom",
          message: getLabel(
            projectAddFeesAndExpensesSiteCoreKeys.exchangeRateError,
          ),
        },
      );

      return false;
    } else {
      clearErrors(
        ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate,
      );
    }
    return true;
  };

  const handleTabSubmit = async () => {
    if (selectedTab === TAB_INDEX.GENERAL) {
      validateGeneralForm();
    } else if (selectedTab === TAB_INDEX.TAX) {
      setSelectedTab(TAB_INDEX.SUMMARY);
    } else if (selectedTab === TAB_INDEX.SUMMARY) {
      onAddFeesOrExpense();
    }
  };

  const onTabChange = (_event: React.ChangeEvent<{}>, value: number) => {
    if (selectedTab === TAB_INDEX.GENERAL) {
      validateGeneralForm();
    } else {
      setSelectedTab(value);
    }
  };

  const handleHideProjectAddFeesAndExpenseModal = () => {
    if (isFormDirty) {
      setState((prev) => ({
        ...prev,
        showCancelModal: true,
      }));
    } else {
      onHideProjectAddFeesAndExpenseModal();
    }
  };

  const closeModalHandler = () => {
    setState((prev) => ({
      ...prev,
      showCancelModal: false,
      showTypeChangeModal: false,
    }));
  };

  const modalTitle = () => {
    if (selectedExpenseDetails) {
      return replaceHashWithValue(
        getLabel(projectAddFeesAndExpensesSiteCoreKeys.editHeading),
        {
          FEEOREXPENSENAME:
            selectedExpenseDetails?.expenseDetails.feeName ?? "",
          TYPE:
            selectedExpenseDetails?.type ===
            ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_EXPENSE
              ? expenseRadioButtonLabel
              : getLabel(
                  ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_OR_PROJECT_FEE,
                ),
        },
      );
    }
    return (
      fetchComponentTitle(
        projectAddFeesAndExpensesSiteCoreKeys.addFeesAndExpenses,
      ) ?? ""
    );
  };

  const handleBackButtonClick = () => {
    setSelectedTab((prev) => prev - 1);
  };

  const handleCancelConfirm = () => {
    if (state.showCancelModal) {
      onHideProjectAddFeesAndExpenseModal();
    } else if (state.showTypeChangeModal) {
      resetFields();

      const newType =
        state.type === ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_OR_PROJECT_FEE
          ? ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_EXPENSE
          : ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_OR_PROJECT_FEE;
      setValue(ProjectAddOrEditExpenseOrFeeFormKey.projectType, newType);

      setState((prev) => ({
        ...prev,
        showTypeChangeModal: false,
        type: newType,
      }));
    }
  };
  const primaryButtonLabel = () => {
    if (selectedTab === TAB_INDEX.SUMMARY) {
      return selectedExpenseDetails ? commonLabels.update : commonLabels.save;
    }
    return commonLabels.next;
  };

  return (
    <>
      <MuiModal
        open={showProjectAddFeesAndExpenseModal}
        onPrimaryButtonClick={handleTabSubmit}
        onClose={handleHideProjectAddFeesAndExpenseModal}
        primaryButtonTitle={primaryButtonLabel()}
        title={modalTitle()}
        secondaryButtonTitle={commonLabels.cancel}
        hasLargeContent
        actionButtonTitle={
          selectedTab !== TAB_INDEX.GENERAL ? commonLabels.back : undefined
        }
        onActionButtonClick={() => handleBackButtonClick()}
        onSecondaryButtonClick={handleHideProjectAddFeesAndExpenseModal}
      >
        <ProjectAddFeesAndExpenseTabWrapper>
          <MuiBox
            className={classNames({
              "summary-header":
                selectedTab === TAB_INDEX.SUMMARY && !selectedExpenseDetails,
              "radio-container":
                selectedTab !== TAB_INDEX.SUMMARY && !selectedExpenseDetails,
            })}
          >
            {!selectedExpenseDetails &&
              (selectedTab === TAB_INDEX.GENERAL ? (
                <MuiRadioGroup
                  label={getLabel(
                    projectAddFeesAndExpensesSiteCoreKeys.selectTypeHeading,
                  )}
                  value={state.type}
                  onChange={handleTypeChange}
                  options={radioOptions}
                  requiredARIALabel={commonARIALabel.required}
                />
              ) : (
                <MuiTypography variant="h3Bold">
                  {getLabel(projectAddFeesAndExpensesSiteCoreKeys.typeHeading)}
                  {state.type ===
                  ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_OR_PROJECT_FEE
                    ? programProjectFeeRadioButtonLabel
                    : expenseRadioButtonLabel}
                </MuiTypography>
              ))}
          </MuiBox>
          <MuiTabs tabs={tabs} value={selectedTab} onChange={onTabChange}>
            <FormProvider {...formMethod}>
              <MuiTabPanel
                panelId={`panel1`}
                isPanelWithMount={true}
                value={selectedTab}
                index={TAB_INDEX.GENERAL}
                ariaLabelledById={tabIdGeneral}
                data-testid="tab-panel-general"
              >
                <ProjectAddFeesAndExpenseGeneralForm
                  currencyList={currencyList}
                  type={state.type}
                  transactionDateValue={
                    selectedExpenseDetails?.expenseDetails?.transactionDate
                  }
                  feeCurrencyValue={
                    selectedExpenseDetails?.expenseDetails?.feeCurrency?.id
                  }
                />
              </MuiTabPanel>
              <MuiTabPanel
                panelId={`panel2`}
                isPanelWithMount={true}
                value={selectedTab}
                index={TAB_INDEX.TAX}
                ariaLabelledById={tabIdTax}
                data-testid="tab-panel-tax"
              >
                <ProjectAddFeesAndExpenseTaxForm />
              </MuiTabPanel>
              <MuiTabPanel
                panelId={`panel3`}
                value={selectedTab}
                index={TAB_INDEX.SUMMARY}
                ariaLabelledById={tabIdSummary}
                data-testid="tab-panel-summary"
              >
                <ProjectAddFeesAndExpenseSummary type={state.type} />
              </MuiTabPanel>
            </FormProvider>
          </MuiTabs>
        </ProjectAddFeesAndExpenseTabWrapper>
      </MuiModal>
      <CancelFieldModal
        title={
          state.showTypeChangeModal ? commonLabels.confirmation : undefined
        }
        showModal={state.showCancelModal || state.showTypeChangeModal}
        cancelMessage={getLabel(
          state.showTypeChangeModal
            ? projectAddFeesAndExpensesSiteCoreKeys.typeChangeConfirmation
            : projectAddFeesAndExpensesSiteCoreKeys.cancelConfirmMessage,
        )}
        handleModalClose={closeModalHandler}
        handlePrimaryButtonClick={handleCancelConfirm}
      />
    </>
  );
};
